import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useDispatch, useSelector } from "react-redux";
import Layout from "./components/Layout.js";
import Dashboard from "./pages/Dashboard";
import AutomationDetail from "./pages/AutomationDetail";
import ScheduleManager from "./pages/ScheduleManager";
import NetworkRules from "./pages/NetworkRules";
import LoginPage from "./pages/LoginPage";
import AppRegistry from "./pages/AppRegistry";
import AppDetail from "./pages/AppDetail";
import AppsOverviewPage from "./pages/AppsOverviewPage";
import ClientDashboard from "./pages/ClientDashboard";
import ClientDetail from "./pages/ClientDetail";
import ClientAppMatrixPage from "./pages/ClientAppMatrixPage";
import EmailInterceptPage from "./pages/EmailInterceptPage";
import ElementHiderPage from "./pages/ElementHiderPage";
import NetworkMonitorPage from "./pages/NetworkMonitorPage";
import FilterSimulatorPage from "./pages/FilterSimulatorPage";
import CredentialVaultPage from "./pages/CredentialVaultPage";
import ExtensionDownloadPage from "./pages/ExtensionDownloadPage";
import OAuthCallback from "./components/auth/OAuthCallback";
import MockupIndex from "./pages/mockups/MockupIndex";
import ExtV5Index from "./pages/mockups/ext-v5/ExtV5Index";
import ExtAdminIndex from "./pages/mockups/ext-admin/ExtAdminIndex";
import IosMockupIndex from "./pages/mockups/ios/IosMockupIndex";
import AndroidMockupIndex from "./pages/mockups/android/AndroidMockupIndex";
import AllMocksIndex from "./pages/mockups/AllMocksIndex";
import ScriptEditorTest from "./pages/ScriptEditorTest";
import Scripts from "./pages/Scripts";
import ScriptEditor from "./pages/ScriptEditor";
import RunDetailPage from "./pages/RunDetailPage";
import ProgressPushRunsPage from "./pages/ProgressPushRunsPage";
import ProgressPushRunDetailPage from "./pages/ProgressPushRunDetailPage";
import ComplianceGoogleOUPage from "./pages/ComplianceGoogleOUPage";
import OuComplianceShell from "./pages/ou-compliance/OuComplianceShell";
import OnboardingWizard from "./pages/onboarding/OnboardingWizard";
// ScriptWatch merged into ScriptEditor — /scripts/:id/watch redirects to /scripts/:id/edit?watch=true
import { checkAuthStatus } from "./redux/thunks/authThunks";
// manualLogin removed — real auth only

const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSelector(state => state.auth);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    // Save where the user wanted to go so LoginPage / OAuthCallback can send
    // them there after auth. Skip saving if we're already on an auth route.
    try {
      const intent = window.location.pathname + window.location.search + window.location.hash;
      if (intent && !intent.startsWith('/login') && !intent.startsWith('/auth/')) {
        sessionStorage.setItem('automatrix.returnTo', intent);
      }
    } catch (e) { /* sessionStorage might be blocked in some contexts */ }
    return <Navigate to="/login" />;
  }

  return children;
};

/** Redirect legacy /scripts/:id/watch to /scripts/:id/edit?watch=true */
const WatchRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/scripts/${id}/edit?watch=true`} replace />;
};

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Don't run checkAuthStatus on the OAuth callback route —
    // OAuthCallback handles its own token storage and auth
    if (!window.location.pathname.startsWith('/auth/callback')) {
      dispatch(checkAuthStatus());
    }

    // Send auth token to extension via bridge (if token exists)
    const token = localStorage.getItem('token');
    if (token) {
      window.postMessage({
        source: 'automatrix-frontend',
        requestId: 'auth-sync-' + Date.now(),
        payload: { action: 'SET_AUTH_TOKEN', token }
      }, '*');
    }
  }, [dispatch]);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/" element={
            <Navigate to="/apps" replace />
          } />
          <Route path="/dashboard" element={
            <Navigate to="/apps" replace />
          } />
          <Route path="/automations" element={
            <Navigate to="/scripts" replace />
          } />
          <Route path="/automations/:id" element={
            <ProtectedRoute>
              <Layout>
                <AutomationDetail />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credentials" element={
            <Navigate to="/credential-vault" replace />
          } />
          <Route path="/schedules" element={
            <ProtectedRoute>
              <Layout>
                <ScheduleManager />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients" element={
            <ProtectedRoute>
              <Layout>
                <ClientDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients/matrix" element={
            <ProtectedRoute>
              <Layout>
                <ClientAppMatrixPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients/:id" element={
            <ProtectedRoute>
              <Layout>
                <ClientDetail />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/apps" element={
            <ProtectedRoute>
              <Layout>
                <AppsOverviewPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/apps/legacy" element={
            <ProtectedRoute>
              <Layout>
                <AppRegistry />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/apps/:configKey" element={
            <ProtectedRoute>
              <Layout>
                <AppDetail />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/extension-scripts" element={
            <Navigate to="/scripts" replace />
          } />
          <Route path="/network-rules" element={
            <ProtectedRoute>
              <Layout>
                <NetworkRules />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/email-intercept" element={
            <ProtectedRoute>
              <Layout>
                <EmailInterceptPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credential-vault" element={
            <ProtectedRoute>
              <Layout>
                <CredentialVaultPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/extension-download" element={
            <ProtectedRoute>
              <Layout>
                <ExtensionDownloadPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/element-hider" element={
            <ProtectedRoute>
              <Layout>
                <ElementHiderPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/network-monitor" element={
            <ProtectedRoute>
              <Layout>
                <NetworkMonitorPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/filter-simulator" element={
            <ProtectedRoute>
              <Layout>
                <FilterSimulatorPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/scripts" element={
            <ProtectedRoute>
              <Layout>
                <Scripts />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/scripts/:id/edit" element={
            <ProtectedRoute>
              <Layout>
                <ScriptEditor />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/scripts/:id/runs/:execId" element={
            <ProtectedRoute>
              <Layout>
                <RunDetailPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/scripts/:id/watch" element={
            <WatchRedirect />
          } />
          <Route path="/scripts/test-editor" element={
            <ProtectedRoute>
              <Layout>
                <ScriptEditorTest />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/progress-push" element={
            <ProtectedRoute>
              <Layout>
                <ProgressPushRunsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/progress-push/runs/:id" element={
            <ProtectedRoute>
              <Layout>
                <ProgressPushRunDetailPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance/google-ou" element={
            <ProtectedRoute>
              <Layout>
                <OuComplianceShell />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/compliance/google-ou/legacy" element={
            <ProtectedRoute>
              <Layout>
                <ComplianceGoogleOUPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/onboarding/google-workspace" element={
            <ProtectedRoute>
              <Layout>
                <OnboardingWizard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups" element={
            <ProtectedRoute>
              <Layout>
                <MockupIndex />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/all" element={
            <ProtectedRoute>
              <Layout>
                <AllMocksIndex />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/ext-v5" element={
            <ProtectedRoute>
              <Layout>
                <ExtV5Index />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/ext-v5/:slug" element={
            <ProtectedRoute>
              <Layout>
                <ExtV5Index />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/ext-admin" element={
            <ProtectedRoute>
              <Layout>
                <ExtAdminIndex />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/ext-admin/:slug" element={
            <ProtectedRoute>
              <Layout>
                <ExtAdminIndex />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/ios" element={
            <ProtectedRoute>
              <Layout>
                <IosMockupIndex />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/android" element={
            <ProtectedRoute>
              <Layout>
                <AndroidMockupIndex />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/mockups/android/:slug" element={
            <ProtectedRoute>
              <Layout>
                <AndroidMockupIndex />
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
